// HorizontalCardProduct.jsx
import React, { useContext, useEffect, useRef, useState } from "react";
import fetchAllCategories from "../../helpers/fetchAllCategories";
import { displayNGNCurrency } from "../../helpers/displayCurrency";
import { FaAngleLeft, FaAngleRight } from "react-icons/fa6";
import { Link } from "react-router-dom";
import addToCart from "../../helpers/addToCart";
import Context from "../../context";
import { useTheme } from "../../components/Theme/ThemeProvider";
import { ShoppingCart, Star, Eye } from "lucide-react";
import { summaryApi } from "../../common";
import LikeButton from '../../components/ProductLike/LikeButton';

function HorizontalCardProduct({ productsCategory, heading }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [scroll, setScroll] = useState(0);
  const [hoveredId, setHoveredId] = useState(null);
  const [productRatings, setProductRatings] = useState({});

  const { fetchUserAddToCart } = useContext(Context);
  const scrollElement = useRef();
  const { colors } = useTheme();

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchAllCategories(productsCategory);
      if (response && response.data) {
        setData(response.data);
        response.data.forEach(product => {
          fetchProductRating(product._id);
        });
      } else {
        setError("No data found");
      }
    } catch (error) {
      console.error(error);
      setError(error.message || "An error occurred");
    }
    setLoading(false);
  };

  const fetchProductRating = async (productId) => {
    try {
      const response = await fetch(`${summaryApi.getReviews.url}/${productId}`, {
        method: summaryApi.getReviews.method,
        credentials: 'include'
      });
      const data = await response.json();
      
      if (data.data && data.data.length > 0) {
        const totalRating = data.data.reduce((sum, review) => sum + review.rating, 0);
        const averageRating = totalRating / data.data.length;
        
        setProductRatings(prev => ({
          ...prev,
          [productId]: {
            average: averageRating,
            count: data.data.length
          }
        }));
      }
    } catch (error) {
      console.error(`Error fetching rating for product ${productId}:`, error);
    }
  }

  useEffect(() => {
    fetchData();
  }, [productsCategory]);

  const scrollRight = () => {
    if (scrollElement.current) {
      const newScrollPosition = scroll + 400;
      scrollElement.current.scrollTo({
        left: newScrollPosition,
        behavior: "smooth",
      });
      setScroll(newScrollPosition);
    }
  };

  const scrollLeft = () => {
    if (scrollElement.current) {
      const newScrollPosition = scroll - 400;
      scrollElement.current.scrollTo({
        left: newScrollPosition,
        behavior: "smooth",
      });
      setScroll(newScrollPosition);
    }
  };

  const handleAddToCart = async (e, productId) => {
    await addToCart(e, productId, fetchUserAddToCart);
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        size={12}
        fill={i < Math.floor(rating) ? colors.primary : i < rating ? colors.primary : colors.border}
        style={{ color: i < Math.floor(rating) ? colors.primary : i < rating ? colors.primary : colors.border }}
      />
    ));
  }

  const SkeletonLoader = () => {
    return Array(6)
      .fill()
      .map((_, index) => (
        <div
          key={index}
          className="min-w-[320px] md:min-w-[360px] h-40 rounded-xl overflow-hidden animate-pulse flex gap-4 p-4"
          style={{
            backgroundColor: colors.bgSecondary,
            border: `1px solid ${colors.border}`
          }}
        >
          <div
            className="w-32 h-32 rounded-lg flex-shrink-0"
            style={{ backgroundColor: colors.bgTertiary }}
          />
          <div className="flex-1 space-y-3">
            <div
              className="h-4 rounded-full w-3/4"
              style={{ backgroundColor: colors.bgTertiary }}
            />
            <div
              className="h-3 rounded-full w-1/2"
              style={{ backgroundColor: colors.bgTertiary }}
            />
            <div
              className="h-6 rounded-full w-2/3"
              style={{ backgroundColor: colors.bgTertiary }}
            />
          </div>
        </div>
      ));
  };

  return (
    <div className="relative group py-4">
      {/* Scroll Navigation Buttons */}
      <button
        onClick={scrollLeft}
        className="absolute -left-5 md:left-0 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full opacity-0 md:opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110 hidden md:flex items-center justify-center"
        style={{
          backgroundColor: colors.primary,
          color: 'white',
          boxShadow: `0 4px 16px ${colors.shadow}`
        }}
        title="Scroll left"
      >
        <FaAngleLeft className="text-xl" />
      </button>

      <button
        onClick={scrollRight}
        className="absolute -right-5 md:right-0 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full opacity-0 md:opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110 hidden md:flex items-center justify-center"
        style={{
          backgroundColor: colors.primary,
          color: 'white',
          boxShadow: `0 4px 16px ${colors.shadow}`
        }}
        title="Scroll right"
      >
        <FaAngleRight className="text-xl" />
      </button>

      {/* Products Container */}
      <div
        className="flex items-center gap-6 overflow-x-auto scrollbar-none scroll-smooth px-2"
        ref={scrollElement}
      >
        {loading ? (
          <SkeletonLoader />
        ) : error ? (
          <div
            className="text-center py-8 w-full"
            style={{ color: colors.textSecondary }}
          >
            {error}
          </div>
        ) : data.length === 0 ? (
          <div
            className="text-center py-8 w-full"
            style={{ color: colors.textSecondary }}
          >
            No products found
          </div>
        ) : (
          data.map((product) => (
            <div
              key={product._id}
              className="min-w-[320px] md:min-w-[360px] group/card transition-all duration-300"
              onMouseEnter={() => setHoveredId(product._id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              {/* Product Card */}
              <div
                className="h-full rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 flex"
                style={{
                  backgroundColor: colors.bgSecondary,
                  border: `1px solid ${colors.border}`
                }}
              >
                {/* Image Container */}
                <div
                  className="relative w-32 md:w-40 h-40 flex-shrink-0 overflow-hidden"
                  style={{ backgroundColor: colors.bgTertiary }}
                >
                  {/* Product Image */}
                  <img
                    src={product.productImage[0]}
                    alt={product.productName}
                    className="w-full h-full object-scale-down p-2 group-hover/card:scale-110 transition-transform duration-300"
                  />

                  {/* Discount Badge */}
                  {product.productDiscount && product.productPrice && (
                    <div
                      className='absolute top-3 right-3 px-3 py-1 rounded-full text-sm font-bold text-white'
                      style={{ backgroundColor: colors.accent }}
                    >
                      -{Math.round((product.productDiscount / product.productPrice) * 100)}%
                    </div>
                  )}

                  {/* Like Button */}
                  <div className="absolute bottom-2 left-2 opacity-0 group-hover/card:opacity-100 transition-all duration-300 z-20 pointer-events-auto">
                    <LikeButton productId={product._id} size={20} />
                  </div>

                  {/* Overlay Gradient */}
                  <div
                     className="absolute inset-0 opacity-0 group-hover/card:opacity-100 transition-opacity duration-300 pointer-events-none"
                    style={{
                      background: `linear-gradient(to right, ${colors.primary}40, transparent)`
                    }}
                  />
                </div>

                {/* Content Container */}
                <div className="flex-1 p-4 flex flex-col justify-between">
                  {/* Product Details */}
                  <div className="space-y-2">
                    {/* Product Name */}
                    <h3
                      className="font-bold text-sm line-clamp-2 transition-colors duration-300"
                      style={{ color: colors.text }}
                    >
                      {product.productName}
                    </h3>

                    {/* Category */}
                    <p
                      className="text-xs capitalize"
                      style={{ color: colors.textSecondary }}
                    >
                      {product.productsCategory}
                    </p>

                    {/* Rating */}
                    <div className="flex items-center gap-1">
                      <div className="flex gap-0.5">
                        {productRatings[product._id] ? (
                          renderStars(productRatings[product._id].average)
                        ) : (
                          [...Array(5)].map((_, i) => (
                            <Star key={i} size={12} style={{ color: colors.border }} />
                          ))
                        )}
                      </div>
                      <span
                        className="text-xs ml-1"
                        style={{ color: colors.textSecondary }}
                      >
                        {productRatings[product._id] ? (
                          `(${productRatings[product._id].average.toFixed(1)}/5)`
                        ) : (
                          '(No reviews)'
                        )}
                      </span>
                    </div>
                  </div>

                  {/* Price and Buttons */}
                  <div className="space-y-3">
                    {/* Price Section */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="font-bold text-base"
                        style={{ color: colors.primary }}
                      >
                        {displayNGNCurrency(product.productPrice)}
                      </span>
                      {product.productDiscount && (
                        <span
                          className="text-xs line-through"
                          style={{ color: colors.textSecondary }}
                        >
                          {displayNGNCurrency(product.productDiscount)}
                        </span>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      {/* Add to Cart Button */}
                      <button
                        onClick={(e) => handleAddToCart(e, product?._id)}
                        className="flex-1 py-2 px-3 rounded-lg font-semibold text-white transition-all duration-300 hover:scale-105 hover:shadow-lg flex items-center justify-center gap-2 text-sm"
                        style={{
                          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`
                        }}
                      >
                        <ShoppingCart size={14} />
                        Add
                      </button>

                      {/* View Button */}
                      <Link
                        to={`/product/${product?._id}`}
                        className="flex-1 py-2 px-3 rounded-lg font-semibold transition-all duration-300 hover:scale-105 hover:shadow-lg flex items-center justify-center gap-2 text-sm"
                        style={{
                          backgroundColor: colors.bgSecondary,
                          color: colors.primary,
                          border: `2px solid ${colors.primary}`,
                        }}
                      >
                        <Eye size={14} />
                        View
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <style jsx>{`
        .scrollbar-none::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-none {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .group:hover .group\/card {
          animation: slideIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default HorizontalCardProduct;